import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";

export type QueuedUpdate = {
  id: string;
  itemKey: string;
  attr: string;
  value: string | number | null;
  status: "queued" | "active";
};

export type BuildingUpdatesState = {
  items: QueuedUpdate[];
  completed: number;
  total: number;
  status: "idle" | "running" | "error";
  error: string | null;
};

const initialState: BuildingUpdatesState = {
  items: [],
  completed: 0,
  total: 0,
  status: "idle",
  error: null,
};

const buildingUpdatesSlice = createSlice({
  name: "buildingUpdates",
  initialState,
  reducers: {
    enqueue(state, action: PayloadAction<QueuedUpdate[]>) {
      for (const update of action.payload) {
        const existing = state.items.find(
          (item) => item.status === "queued" && item.itemKey === update.itemKey && item.attr === update.attr,
        );
        if (existing) {
          existing.id = update.id;
          existing.value = update.value;
        } else {
          state.items.push({ ...update, status: "queued" });
          state.total += 1;
        }
      }
      if (action.payload.length > 0) {
        state.status = "running";
        state.error = null;
      }
    },
    start(state, action: PayloadAction<string>) {
      const item = state.items.find((i) => i.id === action.payload);
      if (item) {
        item.status = "active";
      }
    },
    startMany(state, action: PayloadAction<string[]>) {
      const ids = new Set(action.payload);
      for (const item of state.items) {
        if (ids.has(item.id)) {
          item.status = "active";
        }
      }
    },
    succeed(state, action: PayloadAction<string>) {
      state.items = state.items.filter((i) => i.id !== action.payload);
      state.completed += 1;
    },
    succeedMany(state, action: PayloadAction<string[]>) {
      const ids = new Set(action.payload);
      const before = state.items.length;
      state.items = state.items.filter((i) => !ids.has(i.id));
      state.completed += before - state.items.length;
    },
    fail(state, action: PayloadAction<{ id: string; message: string }>) {
      state.items = [];
      state.status = "error";
      state.error = action.payload.message;
    },
    reset() {
      return initialState;
    },
  },
});

export const selectLockedCellKeys = createSelector(
  (state: { buildingUpdates: BuildingUpdatesState }) => state.buildingUpdates.items,
  (items) => new Set(items.map((item) => `${item.itemKey}:${item.attr}`)),
);

export const { enqueue, start, startMany, succeed, succeedMany, fail, reset } = buildingUpdatesSlice.actions;

export default buildingUpdatesSlice.reducer;
